/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { clearSession } from '@back-office/core';
import {
  ForgotPasswordRequest,
  InvitationCheckingRequest,
  InvitationsRequest,
  InvoiceBody,
  LoginOTPRequest,
  LoginRequest,
  NewPasswordRequest,
  RegisterOTPRequest,
  RegisterRequest,
  VerifyOTPRequest,
} from '@back-office/http';
import { GcpVerticalTabComponent } from '@back-office/ui';
import { cast, flow, Instance, types } from 'mobx-state-tree';
import { OtpVia } from '../components/login/EnterOTP';
import { LoginStep } from '../forms';
import {
  approvedRecurringFileCheckListRequest,
  approvedRecurringFilePartialRequest,
  approvedRecurringFilePendingRequest,
  cancelReasonTypesRequest,
  cancelRefundRequest,
  deleteInvitationRequest,
  deleteUserRequest,
  downloadFileRecurringRequest,
  forgotPassCheckRequest,
  forgotPasswordRequest,
  getInvitationListRequest,
  getPermissionsRequest,
  getPhoneNumberRequest,
  getTransactionCSVRequest,
  getUserListRequest,
  invitationCheckRequest,
  invitationRequest,
  invoiceRequest,
  loginRequest,
  logoutRequest,
  newPasswordRequest,
  PostInvoiceRequest,
  putOtpLoginRequest,
  putOTPRegisterRequest,
  putResendOTPRequest,
  putVerifyOTPRequest,
  recurringDetailRequest,
  recurringEnrollmentReportRequest,
  recurringEnrollmentTypeRequest,
  recurringsListRequest,
  recurringTransactionReportRequest,
  recurringTransactionSearchRequest,
  refundRequestTypesRequest,
  refundTransactionRequest,
  registerRequest,
  rejectReasonTypesRequest,
  rejectRecurringFileRequest,
  requestOTPLoginViaSms,
  requestRefundRequest,
  resendInvitationRequest,
  searchRecurringRequest,
  transactionListRequest,
  transactionRequest,
  updateRecurringRequest,
  uploadRecurringFileRequest,
  userInvitationRequest,
  voidTransactionRequest,
} from '../requests';
import AlertModel from './alert.store';
import AuthModel from './auth.store';
import CancelReasonTypesModel from './cancel-reason-types.store';
import InvitationModel from './invitations.store';
import InvoicesModel from './invoice.store';
import OTPModel from './opt.store';
import PermissionsTypeModel from './permissions.store';
import RecurringDetailModel from './recurring-detail.store';
import RecurringFileModel from './recurring-file.store';
import RecurringRequestModel from './recurring-request.store';
import RecurringTransactionModel from './recurring-transaction.store';
import RejectReasonTypesModel from './reject-reason-types.store';
import RefundRequestTypesModel from './request.store';
import TransactionDetail from './transaction.store';
import TransactionModel from './transactions.store';
import UsersModel from './users.store';

const RootStore = types
  .model('RootStore', {
    invoice: InvoicesModel,
    alert: AlertModel,
    auth: AuthModel,
    transaction: TransactionModel,
    invitations: InvitationModel,
    detail: TransactionDetail,
    users: UsersModel,
    otp: OTPModel,
    isLoading: types.optional(types.boolean, false),
    isProgress: types.optional(types.boolean, false),
    stage: types.optional(types.string, GcpVerticalTabComponent.Transactions),
    loginStep: types.optional(types.string, LoginStep.Login),
    forgotPwdEmail: types.optional(types.string, ''),
    requestTypes: types.optional(RefundRequestTypesModel, {
      list: [{ id: '', name: '', description: '', status: '' }],
    }),
    permissionsList: types.optional(PermissionsTypeModel, {
      list: [{ id: 0, name: '', description: '' }],
    }),
    recurringRequest: RecurringRequestModel,
    recurringFile: RecurringFileModel,
    recurringDetail: RecurringDetailModel,
    rejectReasonTypes: types.optional(RejectReasonTypesModel, {
      list: [{ id: '', name: '', description: '' }],
    }),
    cancelReasonTypes: types.optional(CancelReasonTypesModel, {
      list: [{ id: '', name: '', description: '' }],
    }),
    recurringTransaction: RecurringTransactionModel,
  })
  .actions((self) => ({
    clearSession() {
      clearSession();
      self.loginStep = LoginStep.Login;
      self.stage = GcpVerticalTabComponent.Transactions;
      self.auth.reset();
      self.alert.closeDialog();
    },
  }))
  .actions((self) => {
    const setLoading = (loading: boolean): void => {
      self.isLoading = loading;
    };

    const getInvoice = flow((form: PostInvoiceRequest) =>
      invoiceRequest(self, form)
    );

    const register = flow((form: RegisterRequest) =>
      registerRequest(self, form)
    );

    const login = flow((form: LoginRequest) => loginRequest(self, form));

    const logout = flow((isForceLogout?: boolean) =>
      logoutRequest(self, isForceLogout)
    );

    const sendInvitation = flow((form: InvitationsRequest) =>
      invitationRequest(self, form)
    );

    const checkInvitation = flow(
      (body: InvitationCheckingRequest, goToLogin: () => void) =>
        invitationCheckRequest(self, body, goToLogin)
    );

    const getTransactionList = flow((query: string) =>
      transactionListRequest(self, query)
    );

    const forgotPassword = flow((body: ForgotPasswordRequest) =>
      forgotPasswordRequest(self, body)
    );

    const newPassword = flow(
      (body: NewPasswordRequest, goToLogin: () => void) =>
        newPasswordRequest(self, body, goToLogin)
    );

    const getTransactionDetail = flow((txId: string) =>
      transactionRequest(self, txId)
    );

    const checkForgotPassword = flow(
      (body: InvitationCheckingRequest, goToLogin: () => void) =>
        forgotPassCheckRequest(self, body, goToLogin)
    );

    const voidTransaction = flow((txID: string) =>
      voidTransactionRequest(self, txID)
    );

    const requestRefundTransaction = flow(
      (txID: string, reason: string, reasonType: string) =>
        requestRefundRequest(self, txID, reason, reasonType)
    );
    const cancelRefundTransaction = flow(
      (requestTxApprovalId: string, txID: string) =>
        cancelRefundRequest(self, requestTxApprovalId, txID)
    );

    const refundTransaction = flow((txID: string) =>
      refundTransactionRequest(self, txID)
    );

    const getInvitationList = flow(() => getInvitationListRequest(self));

    const resendInvitation = flow((body: InvitationsRequest) =>
      resendInvitationRequest(self, body)
    );
    const getPhoneNumber = flow((email: string) =>
      getPhoneNumberRequest(email)
    );

    const sendUserInvitation = flow((body: InvitationsRequest) =>
      userInvitationRequest(self, body)
    );

    const deleteInvitation = flow((email: string) =>
      deleteInvitationRequest(self, email)
    );

    const getUserList = flow(() => getUserListRequest(self));

    const deleteUser = flow((email: string) => deleteUserRequest(self, email));

    const requestRegisterOTP = flow((body: RegisterOTPRequest) =>
      putOTPRegisterRequest(self, body)
    );

    const requestLoginOTP = flow((body: LoginOTPRequest) =>
      putOtpLoginRequest(self, body)
    );
    const requestOTPViaSms = flow((email: string) =>
      requestOTPLoginViaSms(self, email)
    );

    const verifyOTP = flow((body: VerifyOTPRequest, otpMode: OtpVia) =>
      putVerifyOTPRequest(self, body, otpMode)
    );

    const resendOtp = flow(() => putResendOTPRequest(self));

    const setStage = (stage: GcpVerticalTabComponent): void => {
      self.stage = stage;
    };

    const setInvoice = (body: InvoiceBody): void => {
      self.invoice = {
        ...self.invoice,
        ...body,
        availableSourceOfFundCodes: cast(body.availableSourceOfFundCodes),
      };
    };

    const setLoginStep = (step: LoginStep): void => {
      self.loginStep = step;
    };

    const getTransactionCSV = flow((from: string, to: string) =>
      getTransactionCSVRequest(self, from, to)
    );

    const getRecurringTransactionReport = flow((query: string) =>
      recurringTransactionReportRequest(self, query)
    );

    const setForgotPwdEmail = (email: string): void => {
      self.forgotPwdEmail = email;
    };

    const getRequestTypes = flow(() => refundRequestTypesRequest(self));

    const getPermissionsList = flow(() => getPermissionsRequest(self));

    const getRecurringsRequestList = flow((query: string) =>
      recurringsListRequest(self, query)
    );

    const getRecurringsEnrollmentTypeList = flow(() =>
      recurringEnrollmentTypeRequest(self)
    );

    const getSearchRecurring = flow((query: string) =>
      searchRecurringRequest(self, query)
    );

    const getRecurringEnrollmentReport = flow((query: string) =>
      recurringEnrollmentReportRequest(self, query)
    );

    const getRecurringDetail = flow((recurringId: string) =>
      recurringDetailRequest(self, recurringId)
    );

    const updateRecurring = flow(
      (recurringId: string, recurringStatus: string, reason: string) =>
        updateRecurringRequest(self, recurringId, recurringStatus, reason)
    );

    const getRejectReasonTypes = flow(() => rejectReasonTypesRequest(self));

    const getCancelReasonTypes = flow(() => cancelReasonTypesRequest(self));

    const uploadRecurringFile = flow((file: FormData, merchantID: string) =>
      uploadRecurringFileRequest(self, file, merchantID)
    );

    const approvedRecurringFilePending = flow(
      (id: string[], requestedAt: string) =>
        approvedRecurringFilePendingRequest(self, id, requestedAt)
    );

    const approvedRecurringFilePartial = flow(
      (id: string[], requestedAt: string) =>
        approvedRecurringFilePartialRequest(self, id, requestedAt)
    );

    const approvedRecurringCheckLists = flow(
      (data: { onboardBatchID: string[] }) =>
        approvedRecurringFileCheckListRequest(self, data)
    );

    const getRecurringTransactionSearch = flow((query: string) =>
      recurringTransactionSearchRequest(self, query)
    );

    const rejectRecurringFile = flow(
      (id: string[], reason: string | undefined) =>
        rejectRecurringFileRequest(self, id, reason)
    );
    const downloadFileRecurring = flow((fileName: string) =>
      downloadFileRecurringRequest(self, fileName)
    );
    return {
      setLoading,
      getInvoice,
      register,
      login,
      logout,
      setStage,
      sendInvitation,
      checkInvitation,
      getTransactionList,
      forgotPassword,
      newPassword,
      getTransactionDetail,
      checkForgotPassword,
      voidTransaction,
      requestRefundTransaction,
      cancelRefundTransaction,
      setInvoice,
      getInvitationList,
      resendInvitation,
      sendUserInvitation,
      deleteInvitation,
      getUserList,
      deleteUser,
      setLoginStep,
      requestRegisterOTP,
      requestLoginOTP,
      verifyOTP,
      resendOtp,
      getTransactionCSV,
      refundTransaction,
      setForgotPwdEmail,
      requestOTPViaSms,
      getPhoneNumber,
      getRequestTypes,
      getPermissionsList,
      getRecurringsRequestList,
      getRecurringsEnrollmentTypeList,
      getRecurringEnrollmentReport,
      getRecurringDetail,
      updateRecurring,
      getRejectReasonTypes,
      getCancelReasonTypes,
      getSearchRecurring,
      getRecurringTransactionReport,
      approvedRecurringFilePending,
      approvedRecurringFilePartial,
      approvedRecurringCheckLists,
      uploadRecurringFile,
      getRecurringTransactionSearch,
      rejectRecurringFile,
      downloadFileRecurring,
    };
  });

export type RootStoreModel = Instance<typeof RootStore>;
export default RootStore;
